import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { RiQuestionLine } from 'react-icons/ri';

interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  submitText?: string;
  cancelText?: string;
  required?: boolean;
  isLoading?: boolean;
  inputType?: 'text' | 'textarea';
}

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  placeholder = '',
  defaultValue = '',
  submitText = 'ตกลง',
  cancelText = 'ยกเลิก',
  required = true,
  isLoading = false,
  inputType = 'text',
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Reset value when modal opens
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      setError('');
      // Focus input after modal opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = () => {
    if (required && !value.trim()) {
      setError('กรุณากรอกข้อมูล');
      return;
    }
    setError('');
    onSubmit(value.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputType === 'text') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="px-4 py-2 rounded-md text-secondary-700 bg-secondary-100 hover:bg-secondary-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
        onClick={onClose}
        disabled={isLoading}
      >
        {cancelText}
      </button>
      <button
        type="button"
        className="px-4 py-2 rounded-md text-white font-medium bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
            <span>กำลังดำเนินการ...</span>
          </div>
        ) : (
          submitText
        )}
      </button>
    </>
  );

  const icon = <RiQuestionLine className="h-6 w-6 text-info-500" />;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={footer}
      icon={icon}
      closeOnClickOutside={!isLoading}
      closeOnEsc={!isLoading}
    >
      <div className="space-y-4">
        <div className="p-4 rounded-md bg-info-50 border-l-4 border-info-500">
          <div className="text-info-700">{message}</div>
        </div>

        <div>
          {inputType === 'textarea' ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError('');
              }}
              placeholder={placeholder}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                error ? 'border-danger-500' : 'border-secondary-300'
              }`}
              rows={3}
              disabled={isLoading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'promptModal-value-error' : undefined}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                error ? 'border-danger-500' : 'border-secondary-300'
              }`}
              disabled={isLoading}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'promptModal-value-error' : undefined}
            />
          )}
          {error && (
            <p id="promptModal-value-error" className="mt-1 text-sm text-danger-600">{error}</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PromptModal;
